import { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Upload, Sparkles, Save, X } from "lucide-react";
import { wobblyCirclePath } from "@/lib/wobbly";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CROP_SIZE = 480; // output png pixels

export default function BloomUploadDialog({ onAdded, children }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("upload"); // upload | configure
  const [mode, setMode] = useState("identified"); // identified | question
  const [imgSrc, setImgSrc] = useState(null);
  const [name, setName] = useState("");
  const [diameter, setDiameter] = useState("");
  const [note, setNote] = useState("");
  const [question, setQuestion] = useState("");
  const [saving, setSaving] = useState(false);
  const [seed, setSeed] = useState(0);
  const previewRef = useRef(null);
  const imgRef = useRef(null);

  const reset = () => {
    setStep("upload"); setMode("identified"); setImgSrc(null); setName(""); setDiameter("");
    setNote(""); setQuestion(""); setSaving(false); setSeed(Math.random() * 100);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!open) reset(); }, [open]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please pick an image");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImgSrc(ev.target.result);
      setStep("configure");
      setSeed(Math.random() * 100);
    };
    reader.readAsDataURL(f);
  };

  // Build a canvas of the cropped wobbly bloom
  const renderCrop = () => {
    const canvas = previewRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !img.naturalWidth) return null;
    canvas.width = CROP_SIZE;
    canvas.height = CROP_SIZE;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, CROP_SIZE, CROP_SIZE);

    // Center square crop of source
    const s = Math.min(img.naturalWidth, img.naturalHeight);
    const sx = (img.naturalWidth - s) / 2;
    const sy = (img.naturalHeight - s) / 2;

    ctx.save();
    // Clip to wobbly circle path centred at canvas centre
    const path = new Path2D(wobblyCirclePath(CROP_SIZE / 2 - 12, { seed, noise: 0.13, points: 36 }));
    ctx.translate(CROP_SIZE / 2, CROP_SIZE / 2);
    ctx.clip(path);
    ctx.translate(-CROP_SIZE / 2, -CROP_SIZE / 2);
    ctx.drawImage(img, sx, sy, s, s, 0, 0, CROP_SIZE, CROP_SIZE);
    ctx.restore();

    // Subtle ink outline around the wobbly edge
    ctx.save();
    ctx.translate(CROP_SIZE / 2, CROP_SIZE / 2);
    ctx.strokeStyle = "rgba(95,107,67,0.55)";
    ctx.lineWidth = 2;
    ctx.stroke(new Path2D(wobblyCirclePath(CROP_SIZE / 2 - 13, { seed, noise: 0.13, points: 36 })));
    ctx.restore();
    return canvas;
  };

  // Re-render whenever inputs change
  useEffect(() => {
    if (step === "configure" && imgSrc) {
      renderCrop();
    }
  }, [step, imgSrc, seed]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!name.trim()) { toast.error(mode === "question" ? "Give it a working name (e.g., Unknown peach ball)" : "Name your variety"); return; }
    const d = Number(diameter);
    if (!d || d < 1 || d > 60) { toast.error("Bloom diameter must be between 1 and 60 cm"); return; }
    if (mode === "question" && !question.trim()) { toast.error("Add a quick note describing the question"); return; }
    const canvas = previewRef.current;
    if (!canvas) { toast.error("Crop is not ready"); return; }
    const dataUrl = canvas.toDataURL("image/png", 0.9);
    if (dataUrl.length > 1_400_000) { toast.error("Image too large — try a smaller original"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/community/blooms", {
        variety_name: name.trim(),
        bloom_diameter_cm: d,
        image_data_url: dataUrl,
        note: note.trim() || null,
        kind: mode,
        question_text: mode === "question" ? question.trim() : null,
      });
      toast.success(mode === "question" ? "Question posted — keepers will help identify" : `${data.variety_name} added`);
      onAdded?.(data);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add bloom");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        className="bg-cream-soft border-heritage-border max-w-md"
        data-testid="bloom-upload-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-3xl text-sage-dark">
            {step === "upload" ? "Add your bloom" : "Crop your bloom"}
          </DialogTitle>
          <DialogDescription className="font-display italic text-ink-soft text-lg">
            {step === "upload"
              ? "Pick a photograph, then tell us how big the real flower is."
              : "We circle-crop with a wobbly hand-drawn edge."}
          </DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <label
            className="h-card mt-2 border-dashed border-2 border-heritage-border p-8 flex flex-col items-center justify-center text-center cursor-pointer hover:bg-cream-deep/40 transition-colors"
            data-testid="bloom-upload-drop"
          >
            <Upload size={28} className="text-sage-dark" />
            <span className="font-display text-2xl text-sage-dark mt-2">Choose a photo</span>
            <span className="text-sm text-ink-soft mt-1">JPG, PNG, HEIC up to 8 MB</span>
            <input
              type="file"
              accept="image/*"
              onChange={onFile}
              className="hidden"
              data-testid="bloom-upload-input"
            />
          </label>
        )}

        {step === "configure" && (
          <div className="space-y-3">
            <div className="flex justify-center">
              {/* hidden source img used for canvas */}
              <img
                ref={imgRef}
                src={imgSrc}
                alt="source"
                className="hidden"
                onLoad={() => renderCrop()}
              />
              <canvas
                ref={previewRef}
                width={CROP_SIZE}
                height={CROP_SIZE}
                className="w-56 h-56 rounded-full"
                data-testid="bloom-crop-canvas"
              />
            </div>
            <div className="flex justify-center gap-2">
              <button
                type="button"
                className="chip"
                onClick={() => setSeed(Math.random() * 100)}
                data-testid="bloom-rewobble"
              >
                <Sparkles size={13} /> Re-wobble the edge
              </button>
              <button
                type="button"
                className="chip"
                onClick={() => { setStep("upload"); setImgSrc(null); }}
                data-testid="bloom-change-photo"
              >
                <X size={13} /> Different photo
              </button>
            </div>

            <label className="block mt-2">
              <span className="text-sm text-ink-soft mb-1.5 inline-block">Posting as *</span>
              <div className="flex gap-2" data-testid="bloom-mode">
                <button type="button" className="chip" data-active={mode === "identified"} onClick={() => setMode("identified")} data-testid="bloom-mode-identified">
                  Known variety
                </button>
                <button type="button" className="chip" data-active={mode === "question"} onClick={() => setMode("question")} data-testid="bloom-mode-question">
                  ? What is this
                </button>
              </div>
            </label>

            <label className="block mt-2">
              <span className="text-sm text-ink-soft mb-1.5 inline-block">{mode === "question" ? "Working name *" : "Variety name *"}</span>
              <input
                className="field" placeholder="e.g., Café au Lait"
                value={name} onChange={(e) => setName(e.target.value)}
                data-testid="bloom-name" required
              />
            </label>
            <label className="block">
              <span className="text-sm text-ink-soft mb-1.5 inline-block">Bloom diameter (cm) *</span>
              <input
                type="number" min="1" max="60" step="0.5"
                className="field" placeholder="e.g., 22"
                value={diameter} onChange={(e) => setDiameter(e.target.value)}
                data-testid="bloom-diameter" required
              />
              <span className="text-xs text-ink-soft mt-1 inline-block italic">
                Measure across the widest petals. We&apos;ll scale everyone&apos;s blooms to true relative size.
              </span>
            </label>
            <label className="block">
              <span className="text-sm text-ink-soft mb-1.5 inline-block">A small note (optional)</span>
              <input
                className="field" placeholder="e.g., First bloom of the season"
                value={note} onChange={(e) => setNote(e.target.value)}
                data-testid="bloom-note"
              />
            </label>

            {mode === "question" && (
              <label className="block">
                <span className="text-sm text-ink-soft mb-1.5 inline-block">Your question *</span>
                <textarea
                  className="field resize-y" rows={3}
                  placeholder="e.g., Any ideas? Bought as 'Café au Lait' but doesn't look right..."
                  value={question} onChange={(e) => setQuestion(e.target.value)}
                  data-testid="bloom-question"
                />
              </label>
            )}

            <div className="pt-2 flex justify-end gap-2">
              <button className="btn-ghost" onClick={() => setOpen(false)} data-testid="bloom-cancel">Cancel</button>
              <button className="btn-heritage" onClick={submit} disabled={saving} data-testid="bloom-submit">
                <Save size={14} /> {saving ? "Adding..." : "Pin to the flat lay"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
